import { useCallback, useState } from 'react'
import { useDropzone } from 'react-dropzone'
import { useUploadDocument } from '@/hooks/useDocuments'
import { extractionService } from '@/services/extraction.service'
import { cn } from '@/lib/utils'
import { Button } from '@/components/ui/button'
import { Progress } from '@/components/ui/progress'
import { Upload, FileText, X, AlertCircle, CheckCircle, Lock } from 'lucide-react'
import { toast } from 'sonner'

interface UploadDropzoneProps {
  submissionId: string
  onUploadComplete?: () => void
}

interface UploadingFile {
  id: string
  file: File
  progress: number
  status: 'uploading' | 'complete' | 'error'
  error?: string
}

const ALLOWED_TYPES = {
  'application/pdf': ['.pdf'],
  'image/png': ['.png'],
  'image/jpeg': ['.jpg', '.jpeg'],
  'image/tiff': ['.tif', '.tiff']
}

export function UploadDropzone({ submissionId, onUploadComplete }: UploadDropzoneProps) {
  const [uploadingFiles, setUploadingFiles] = useState<UploadingFile[]>([])
  const uploadMutation = useUploadDocument()
  const uploadEnabled = extractionService.isUploadEnabled()

  const onDrop = useCallback(
    async (acceptedFiles: File[]) => {
      if (!uploadEnabled) {
        toast.error('Uploads are disabled. Contact admin for access.')
        return
      }

      const newFiles: UploadingFile[] = acceptedFiles.map((file) => ({
        id: crypto.randomUUID(),
        file,
        progress: 0,
        status: 'uploading' as const
      }))

      setUploadingFiles((prev) => [...prev, ...newFiles])

      for (const uploadFile of newFiles) {
        try {
          await uploadMutation.mutateAsync({
            submissionId,
            file: uploadFile.file,
            onProgress: (progress) => {
              setUploadingFiles((prev) =>
                prev.map((f) =>
                  f.id === uploadFile.id
                    ? { ...f, progress: progress.percentage }
                    : f
                )
              )
            }
          })

          setUploadingFiles((prev) =>
            prev.map((f) =>
              f.id === uploadFile.id
                ? { ...f, progress: 100, status: 'complete' }
                : f
            )
          )

          toast.success(`Uploaded ${uploadFile.file.name}`)
        } catch (err) {
          const errorMessage =
            err instanceof Error ? err.message : 'Upload failed'
          setUploadingFiles((prev) =>
            prev.map((f) =>
              f.id === uploadFile.id
                ? { ...f, status: 'error', error: errorMessage }
                : f
            )
          )
          toast.error(`Failed to upload ${uploadFile.file.name}`)
        }
      }

      onUploadComplete?.()
    },
    [submissionId, uploadMutation, onUploadComplete, uploadEnabled]
  )

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: ALLOWED_TYPES,
    maxSize: 50 * 1024 * 1024, // 50MB
    disabled: !uploadEnabled
  })

  const removeFile = (id: string) => {
    setUploadingFiles((prev) => prev.filter((f) => f.id !== id))
  }

  const activeUploads = uploadingFiles.filter((f) => f.status === 'uploading')

  // Show locked state if uploads are disabled
  if (!uploadEnabled) {
    return (
      <div className="flex flex-col items-center justify-center rounded-lg border-2 border-dashed border-muted-foreground/25 p-8 text-center">
        <Lock className="mb-4 h-10 w-10 text-muted-foreground/50" />
        <p className="mb-2 text-sm font-medium text-muted-foreground">
          Uploads Disabled
        </p>
        <p className="text-xs text-muted-foreground">
          Contact admin to enable upload access.
        </p>
      </div>
    )
  }

  return (
    <div className="space-y-4">
      <div
        {...getRootProps()}
        className={cn(
          'flex cursor-pointer flex-col items-center justify-center rounded-lg border-2 border-dashed p-8 transition-colors',
          isDragActive
            ? 'border-primary bg-primary/5'
            : 'border-muted-foreground/25 hover:border-primary/50'
        )}
      >
        <input {...getInputProps()} />
        <Upload
          className={cn(
            'mb-4 h-10 w-10',
            isDragActive ? 'text-primary' : 'text-muted-foreground'
          )}
        />
        <p className="mb-2 text-sm font-medium">
          {isDragActive
            ? 'Drop files here'
            : 'Drag & drop files here, or click to select'}
        </p>
        <p className="text-xs text-muted-foreground">
          PDF, PNG, JPEG, or TIFF up to 50MB
        </p>
      </div>

      {/* Upload Progress */}
      {uploadingFiles.length > 0 && (
        <div className="space-y-2">
          {uploadingFiles.map((uploadFile) => (
            <div
              key={uploadFile.id}
              className="flex items-center gap-3 rounded-lg border p-3"
            >
              <FileText className="h-8 w-8 text-muted-foreground" />
              <div className="flex-1 min-w-0">
                <p className="truncate text-sm font-medium">
                  {uploadFile.file.name}
                </p>
                {uploadFile.status === 'uploading' && (
                  <Progress value={uploadFile.progress} className="mt-1 h-1" />
                )}
                {uploadFile.status === 'error' && (
                  <p className="flex items-center gap-1 text-xs text-destructive">
                    <AlertCircle className="h-3 w-3" />
                    {uploadFile.error}
                  </p>
                )}
                {uploadFile.status === 'complete' && (
                  <p className="flex items-center gap-1 text-xs text-green-600">
                    <CheckCircle className="h-3 w-3" />
                    Uploaded
                  </p>
                )}
              </div>
              <Button
                variant="ghost"
                size="icon"
                className="h-8 w-8"
                onClick={() => removeFile(uploadFile.id)}
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
          ))}
        </div>
      )}

      {activeUploads.length > 0 && (
        <p className="text-center text-sm text-muted-foreground">
          Uploading {activeUploads.length} file
          {activeUploads.length > 1 ? 's' : ''}...
        </p>
      )}
    </div>
  )
}
